import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, Shuffle, Loader2, ScrollText } from "lucide-react";
import { motion } from "framer-motion";
import { getRandom, quickSearch, trackEvent } from "@/lib/api";
import { ArchivistGlass } from "@/components/brand/ArchivistGlass";
import fullLogoSrc from "@/assets/codex-logo-full.png";

// Subtly highlight the matched portion of a title without bright search styling.
function Highlight({ text, query }) {
  if (!text) return null;
  const q = (query || "").trim();
  if (!q) return <>{text}</>;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <span className="text-gold-light">{text.slice(i, i + q.length)}</span>
      {text.slice(i + q.length)}
    </>
  );
}

export function HeroSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null); // null = untouched, [] = searched/empty
  const [related, setRelated] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const startedRef = useRef(false);

  // Live Archive search — begins at 2 characters, debounced, reuses /api/search.
  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) { setResults(null); setOpen(false); setLoading(false); return; }
    setLoading(true); setOpen(true);
    const t = setTimeout(async () => {
      try {
        if (!startedRef.current) { trackEvent({ event: "home_search_started" }); startedRef.current = true; }
        const d = await quickSearch(query);
        const items = (d.items || []).slice(0, 8);
        setResults(items); setRelated(d.related || []); setActive(-1);
        trackEvent({ event: "search_query_entered", meta: { length: query.length, count: items.length } });
        if (items.length === 0) trackEvent({ event: "no_search_result" });
      } catch (e) { setResults([]); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const openResult = (slug) => { trackEvent({ event: "live_result_opened", meta: { slug } }); navigate(`/topic/${slug}`); };

  const askArchivistAbout = (source) => {
    const query = q.trim();
    trackEvent({ event: source === "results" ? "ask_archivist_from_search" : "ask_archivist_from_home", meta: { has_query: !!query } });
    navigate(query ? `/archivist?q=${encodeURIComponent(query)}` : "/archivist");
  };

  const surpriseMe = async () => {
    try { const r = await getRandom(); navigate(`/topic/${r.slug}`); } catch (e) { /* ignore */ }
  };

  const onKeyDown = (e) => {
    const n = results?.length || 0;
    const total = n + 1; // + the "Ask the Archivist" row
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((a) => Math.min(total - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(-1, a - 1)); }
    else if (e.key === "Enter") {
      if (active >= 0 && active < n) { e.preventDefault(); openResult(results[active].slug); }
      else if (active === n) { e.preventDefault(); askArchivistAbout("results"); }
      // With no selection we intentionally do nothing — a search is never auto-sent to the Archivist.
    } else if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); }
  };

  return (
    <div className="relative isolate overflow-hidden">
      <div className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-8 pt-20 pb-16 md:pt-28 md:pb-24 text-center">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}>
          <div className="flex flex-col items-center" data-testid="hero-brand-lockup">
            <div className="relative inline-block">
              <span aria-hidden className="codex-chrome-glow" />
              <img src={fullLogoSrc} alt="CODEX — Archive of the Unexplained" draggable={false}
                className="relative block w-[300px] select-none sm:w-[380px] lg:w-[440px]" style={{ height: "auto" }} />
            </div>
          </div>
          <h1 className="mt-8 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-foreground/90">
            What mystery would you like to <span className="gold-text">explore</span>?
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm md:text-base leading-7 text-muted-foreground">
            Follow the thread through history, science, archaeology, Scripture, and the unexplained,
            with evidence clearly separated from interpretation.
          </p>
          <div className="mt-4 font-mono text-[11px] uppercase tracking-[0.34em] text-gold-soft/70">Discover. Discern. Decide.</div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15, ease: [0.2, 0.8, 0.2, 1] }}
          className="mx-auto mt-9 max-w-2xl">
          {/* Live Archive search */}
          <div ref={wrapRef} className="relative">
            <div className="flex items-center gap-2 rounded-full codex-glass-strong px-4 py-2.5">
              <Search className="h-5 w-5 shrink-0 text-gold-light" />
              <input
                ref={inputRef}
                data-testid="hero-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onFocus={() => { if ((q.trim().length >= 2) && results) setOpen(true); }}
                onKeyDown={onKeyDown}
                placeholder="Search case files, topics, people, places…"
                aria-label="Search the Archive"
                role="combobox"
                aria-expanded={open}
                aria-controls="hero-search-results"
                className="h-11 w-full bg-transparent text-[15px] text-foreground placeholder:text-muted-foreground/70 focus:outline-none"
              />
              {loading ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gold-light/70" /> : null}
            </div>

            {open && results !== null ? (
              <div id="hero-search-results" data-testid="hero-search-results" role="listbox"
                className="absolute left-0 right-0 z-30 mt-2 overflow-hidden rounded-2xl border border-gold-light/25 bg-[rgba(8,14,26,0.98)] text-left shadow-2xl backdrop-blur-sm">
                {results.length > 0 ? (
                  <ul className="max-h-[52vh] divide-y divide-white/5 overflow-y-auto">
                    {results.map((r, i) => (
                      <li key={r.slug}>
                        <button
                          data-testid={`hero-result-${r.slug}`}
                          onMouseEnter={() => setActive(i)}
                          onClick={() => openResult(r.slug)}
                          className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors ${active === i ? "bg-gold-light/[0.08]" : "hover:bg-white/[0.03]"}`}>
                          <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-md border border-gold-light/20 bg-white/[0.03]">
                            {r.hero_image ? (
                              <img src={r.hero_image} alt="" className="h-full w-full object-cover" loading="lazy" />
                            ) : (
                              <ScrollText className="h-5 w-5 text-gold-light/50" />
                            )}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-baseline justify-between gap-3">
                              <span className="truncate font-serif text-[15px] text-parchment"><Highlight text={r.title} query={q} /></span>
                              {r.category ? <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground/70">{r.category}</span> : null}
                            </span>
                            {r.summary ? <span className="mt-0.5 line-clamp-1 text-[12.5px] leading-6 text-muted-foreground">{r.summary}</span> : null}
                            {(r.is_unsealed || r.is_explorer_free) ? (
                              <span className="mt-1 flex flex-wrap items-center gap-1.5">
                                {r.is_unsealed ? <span className="rounded-full border border-gold-light/40 bg-gold-light/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em] text-gold-light">Unsealed today</span> : null}
                                {r.is_explorer_free ? <span className="rounded-full border border-emerald-300/30 bg-emerald-300/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em] text-emerald-200">Explorer Access</span> : null}
                              </span>
                            ) : null}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="px-4 py-4 text-[13px] text-muted-foreground/80" data-testid="hero-search-empty">
                    No matching case file has been cataloged yet.
                  </div>
                )}

                {/* Related Files — connected records surfaced via the Knowledge Universe */}
                {related.length > 0 ? (
                  <div className="border-t border-white/5 px-4 py-2.5" data-testid="hero-search-related">
                    <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/60">Related Files</div>
                    <ul className="space-y-0.5">
                      {related.map((r) => (
                        <li key={r.slug}>
                          <button data-testid={`hero-related-${r.slug}`} onClick={() => openResult(r.slug)}
                            className="flex w-full items-center gap-2 rounded-md px-1 py-1 text-left text-[13px] text-muted-foreground transition-colors hover:text-gold-light">
                            <span className="text-gold-light/50">•</span>
                            <span className="truncate">{r.title}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {/* Hand off to the Archivist with the typed text preserved (never auto-sent) */}
                <button
                  data-testid="hero-ask-from-results"
                  onMouseEnter={() => setActive(results.length)}
                  onClick={() => askArchivistAbout("results")}
                  className={`flex w-full items-center gap-2 border-t border-gold-light/20 px-4 py-3 text-left text-[13px] transition-colors ${active === results.length ? "bg-gold-light/[0.1]" : "hover:bg-gold-light/[0.06]"}`}>
                  <ArchivistGlass className="h-4 w-4 shrink-0 text-gold-light" />
                  <span className="min-w-0 truncate text-gold-soft">Ask The Archivist about “{q.trim()}”</span>
                  <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-gold-light" />
                </button>
              </div>
            ) : null}
          </div>

          {/* Separate, secondary Ask The Archivist action */}
          <button
            data-testid="hero-ask-archivist-button"
            onClick={() => askArchivistAbout("home")}
            className="group mt-3 flex w-full items-center justify-center gap-3 rounded-full border border-gold-light/40 bg-gold-light/[0.05] px-5 py-3 text-left transition-colors hover:bg-gold-light/[0.1]">
            <ArchivistGlass className="h-5 w-5 shrink-0 text-gold-light" />
            <span className="min-w-0">
              <span className="block font-mono text-[11px] uppercase tracking-[0.22em] text-gold-light">Ask The Archivist</span>
              <span className="block text-[12.5px] text-muted-foreground">Ask a question. Follow the evidence.</span>
            </span>
            <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-gold-light transition-transform group-hover:translate-x-0.5" />
          </button>
        </motion.div>

        <div className="mt-5 flex items-center justify-center">
          <button onClick={surpriseMe} data-testid="home-hero-surprise-button"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.02] px-4 py-2 text-sm text-muted-foreground transition-colors hover:border-gold-light/40 hover:text-foreground">
            <Shuffle className="h-4 w-4 text-gold-light" /> Surprise Me
          </button>
        </div>
      </div>
    </div>
  );
}
